import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { coverEntryMessages } from './coverEntryMessages';
import { offersCoverEditor } from './coverFieldState';
import { useOpenCoverEditor } from './useOpenCoverEditor';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WizardCoverOfferProps {
  accountId: number;
  /** The catalog row of the page. A wizard with no row (a typed song) offers nothing. */
  catalogItemUuid: string | undefined;
  hasCover: boolean;
}

/**
 * The cover offer of the pre-save wizard and of the SmartLink sheet (design cover-editor a4): they
 * read the cover of the song, so they offer the editor of the song. It opens in a new tab, so the
 * wizard keeps its state; the wizard reads the new cover when the user comes back. Not in a native
 * app's web view (the apps keep their upload).
 */
const WizardCoverOfferComponent: React.FC<WizardCoverOfferProps> = ({ accountId, catalogItemUuid, hasCover }) => {
  const intl = useIntl();
  const isOffered = offersCoverEditor({ isNativeEmbed: useIsNativeEmbed() });
  const open = useOpenCoverEditor({ accountId, newTab: true });
  const onOpen = useCallback(() => {
    if (catalogItemUuid !== undefined) {
      open(catalogItemUuid);
    }
  }, [catalogItemUuid, open]);
  if (catalogItemUuid === undefined || !isOffered) {
    return null;
  }
  return (
    <div className={styles.notice}>
      <span className={styles.column}>
        <b>{intl.formatMessage(hasCover ? coverEntryMessages.wizardFromSong : coverEntryMessages.wizardNone)}</b>
        {!hasCover && <span className={styles.hint}>{intl.formatMessage(coverEntryMessages.wizardNoneHint)}</span>}
      </span>
      <SMLButton
        size="sm"
        outline={hasCover}
        omitCap
        onClick={onOpen}
        hoverTooltip={intl.formatMessage(coverEntryMessages.newTab)}
      >
        {intl.formatMessage(hasCover ? coverEntryMessages.edit : coverEntryMessages.design)}
      </SMLButton>
    </div>
  );
};

export const WizardCoverOffer = TypedMemo(WizardCoverOfferComponent);
